// Pulse Market Intelligence API client
// Backend: tradecraft-pulse (separate Go service)
// Ingress: /api/v1/pulse/* → rewritten to /api/v1/* on pulse-api:8085

const PULSE_BASE = import.meta.env.VITE_PULSE_API_URL || "/api/v1/pulse";

function generateCorrelationId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`;
}

async function pulseRequest<T>(endpoint: string): Promise<T> {
  const correlationId = generateCorrelationId();
  const url = `${PULSE_BASE}${endpoint}`;

  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      "X-Correlation-ID": correlationId,
    },
  });

  if (!response.ok) {
    throw new Error(`Pulse API error: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

// ── Types ──────────────────────────────────────────────────────

export interface Quote {
  symbol: string;
  name: string;
  price: number;
  change: number;
  change_percent: number;
  previous_close: number;
  open: number;
  day_high: number;
  day_low: number;
  volume: number;
  market_cap?: number;
  fifty_two_week_high?: number;
  fifty_two_week_low?: number;
  market_state: string;
}

export interface OHLCV {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface ChartResult {
  symbol: string;
  currency: string;
  range: string;
  interval: string;
  candles: OHLCV[];
}

export interface NewsArticle {
  category: string;
  datetime: number;
  headline: string;
  id: number;
  image: string;
  related: string;
  source: string;
  summary: string;
  url: string;
}

export interface SectorPerformance {
  symbol: string;
  name: string;
  price: number;
  change_percent: number;
  volume: number;
}

export interface FearGreedData {
  score: number;
  rating: string;
  previous_close: number;
  one_week_ago: number;
  one_month_ago: number;
  one_year_ago: number;
}

export interface VIXData {
  price: number;
  change: number;
  change_percent: number;
}

export interface EarningsEvent {
  date: string;
  symbol: string;
  eps_estimate: number;
  eps_actual?: number;
  revenue_estimate?: number;
  revenue_actual?: number;
  hour: string;
}

export interface InsiderTransaction {
  symbol: string;
  name: string;
  share: number;
  change: number;
  transaction_date: string;
  transaction_type: string;
  filing_date: string;
}

export interface Coin {
  id: string;
  symbol: string;
  name: string;
  current_price: number;
  market_cap: number;
  market_cap_rank: number;
  price_change_24h: number;
  price_change_percentage_24h: number;
  high_24h: number;
  low_24h: number;
  total_volume: number;
  image: string;
}

export interface TrendingCoin {
  id: string;
  name: string;
  symbol: string;
  market_cap_rank: number;
  thumb: string;
  price_btc: number;
}

export interface TrendingSymbol {
  symbol: string;
  title: string;
  watchlist_count: number;
}

export interface SymbolSentiment {
  symbol: string;
  bullish: number;
  bearish: number;
  total_messages: number;
  bullish_pct: number;
}

export interface ResponseMeta {
  source: string;
  cached: boolean;
  cached_at?: string;
}

// ── Response Shapes ────────────────────────────────────────────

export interface MarketOverview {
  indices: Quote[];
  futures: Quote[];
  global: Quote[];
  meta: ResponseMeta;
}

export interface MoversResult {
  gainers: Quote[];
  losers: Quote[];
  most_active: Quote[];
}

export interface NewsResponse {
  articles: NewsArticle[];
  meta: ResponseMeta;
}

export interface SectorsResponse {
  sectors: SectorPerformance[];
  meta: ResponseMeta;
}

export interface SentimentOverview {
  fear_greed?: FearGreedData;
  vix?: VIXData;
  meta: ResponseMeta;
}

export interface SocialSentimentResponse {
  trending: TrendingSymbol[];
  sentiment?: SymbolSentiment[];
  meta: ResponseMeta;
}

export interface YieldData {
  treasury_2y: string;
  treasury_5y: string;
  treasury_10y: string;
  treasury_30y: string;
  spread_10y_2y: string;
  fed_funds: string;
  meta: ResponseMeta;
}

export interface CommoditiesData {
  commodities: Quote[];
  meta: ResponseMeta;
}

export interface CurrenciesData {
  currencies: Quote[];
  meta: ResponseMeta;
}

export interface EarningsResponse {
  events: EarningsEvent[];
  meta: ResponseMeta;
}

export interface InsiderResponse {
  transactions: InsiderTransaction[];
  meta: ResponseMeta;
}

export interface CryptoResponse {
  coins: Coin[];
  meta: ResponseMeta;
}

export interface CryptoTrendingResponse {
  coins: TrendingCoin[];
  meta: ResponseMeta;
}

// ── API Functions ──────────────────────────────────────────────

export function getMarketOverview(): Promise<MarketOverview> {
  return pulseRequest("/market/overview");
}

export function getChart(symbol: string, range = "1d"): Promise<ChartResult> {
  return pulseRequest(`/chart/${encodeURIComponent(symbol)}?range=${range}`);
}

export function getNews(symbol?: string): Promise<NewsResponse> {
  const qs = symbol ? `?symbol=${encodeURIComponent(symbol)}` : "";
  return pulseRequest(`/news${qs}`);
}

export function getMovers(): Promise<MoversResult> {
  return pulseRequest("/movers");
}

export function getSectors(): Promise<SectorsResponse> {
  return pulseRequest("/sectors");
}

export function getSentiment(): Promise<SentimentOverview> {
  return pulseRequest("/sentiment");
}

export function getSocialSentiment(): Promise<SocialSentimentResponse> {
  return pulseRequest("/sentiment/social");
}

export function getYields(): Promise<YieldData> {
  return pulseRequest("/economy/yields");
}

export function getCommodities(): Promise<CommoditiesData> {
  return pulseRequest("/economy/commodities");
}

export function getCurrencies(): Promise<CurrenciesData> {
  return pulseRequest("/economy/currencies");
}

export function getEarnings(from?: string, to?: string): Promise<EarningsResponse> {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString() ? `?${params}` : "";
  return pulseRequest(`/earnings${qs}`);
}

export function getInsider(symbol = "AAPL"): Promise<InsiderResponse> {
  return pulseRequest(`/insider?symbol=${encodeURIComponent(symbol)}`);
}

export function getCrypto(limit = 20): Promise<CryptoResponse> {
  return pulseRequest(`/crypto?limit=${limit}`);
}

export function getCryptoTrending(): Promise<CryptoTrendingResponse> {
  return pulseRequest("/crypto/trending");
}
